import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Switch,
  DialogContentText,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  alpha,
  InputBase,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as SearchClearIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PauseCircleFilled as PauseCircleFilledIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { DataGrid, GridColDef, GridFilterModel, GridFilterItem, GridFilterInputValue, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { getConsumers, updateConsumerStatus, getConsumerTransactions, Consumer, ConsumerTransaction } from '../services/consumerService';

// Función para formatear fechas
const formatDate = (value: string) => {
  if (!value || value === '' || value === 'null' || value === 'undefined') return 'Sin definir';
  
  console.log('Formateando fecha:', value);
  
  // Si la fecha viene en formato 'dd/MM/yy HH:mm', la convertimos a 'dd/MM/yyyy HH:mm'
  const shortYearMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{2}) (\d{2}):(\d{2})$/);
  if (shortYearMatch) {
    const [_, day, month, year, hours, minutes] = shortYearMatch;
    const fullYear = parseInt(year) < 30 ? `20${year}` : `19${year}`; // Asume años 20xx para años < 30
    const formatted = `${day}/${month}/${fullYear} ${hours}:${minutes}`;
    console.log('Fecha formateada (formato corto):', formatted);
    return formatted;
  }
  
  // Si la fecha ya viene en formato 'dd/MM/yyyy HH:mm', la devolvemos tal cual
  if (/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}(?::\d{2})?$/.test(value)) {
    console.log('Fecha ya en formato correcto:', value);
    return value;
  }
  
  // Manejar formato ISO (YYYY-MM-DD HH:mm:ss o YYYY-MM-DDTHH:mm:ss)
  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2}):?(\d{2})?/);
  if (isoMatch) {
    const [_, year, month, day, hours, minutes] = isoMatch;
    const formatted = `${day}/${month}/${year} ${hours}:${minutes}`;
    console.log('Fecha formateada (formato ISO):', formatted);
    return formatted;
  }
  
  // Si por alguna razón el formato es diferente, lo intentamos formatear
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      console.log('Fecha inválida:', value);
      return 'Formato inválido';
    }
    const formatted = date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    console.log('Fecha formateada (Date object):', formatted);
    return formatted;
  } catch (error) {
    console.error('Error formateando fecha:', error, 'Valor:', value);
    return 'Error';
  }
};

type ConsumerStatus = 'active' | 'inactive' | 'suspended';

// Estilos personalizados para los filtros
const StyledInputBase = styled(InputBase)(({ theme }) => ({
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(1)})`,
    width: '100%',
    '&:focus': {
      backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
  },
}));

// Componente de filtro personalizado
const CustomFilterInput = ({ item, applyValue }: { item: GridFilterItem; applyValue: (value: any) => void }) => {
  return (
    <StyledInputBase
      autoFocus
      fullWidth
      placeholder={`Filtrar ${item.field || ''}`}
      onChange={(e) => {
        applyValue({ ...item, value: e.target.value });
      }}
      value={item.value || ''}
    />
  );
};

// Componente de filtro para el estado
const StatusFilterInput = ({ item, applyValue }: { item: GridFilterItem; applyValue: (value: any) => void }) => {
  return (
    <FormControl fullWidth variant="outlined" size="small">
      <Select
        value={item.value || ''}
        onChange={(e) => {
          applyValue({ ...item, value: e.target.value || undefined });
        }}
        displayEmpty
        inputProps={{ 'aria-label': 'Seleccionar estado' }}
      >
        <MenuItem value="">
          <em>Todos</em>
        </MenuItem>
        <MenuItem value="active">Activo</MenuItem>
        <MenuItem value="inactive">Inactivo</MenuItem>
      </Select>
    </FormControl>
  );
};

// Interfaz para los datos de promoción del consumidor
interface ConsumerPromotion {
  id: number;
  date: string;
  promotion_name: string;
  promo_code: string;
  client_id: number;
  client_name: string;
  status: string;
}

export default function ConsumersPage() {
  const [consumers, setConsumers] = useState<Consumer[]>([]);
  const [filteredConsumers, setFilteredConsumers] = useState<Consumer[]>([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedConsumerId, setSelectedConsumerId] = useState<string | null>(null);
  const [consumerPromotions, setConsumerPromotions] = useState<ConsumerPromotion[]>([]);
  const [promotionsLoading, setPromotionsLoading] = useState(false);
  const [promotionsError, setPromotionsError] = useState<string | null>(null);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: (() => void) | null;
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  });
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();


  // Cargar transacciones del consumidor
  const fetchConsumerTransactions = async (consumerId: string) => {
    try {
      setPromotionsLoading(true);
      setPromotionsError(null);
      
      // Llamada a la API real
      const transactions = await getConsumerTransactions(consumerId);
      
      // Mapear las transacciones al formato esperado por la interfaz
      const formattedTransactions: ConsumerPromotion[] = transactions.map((tx, index) => ({
        id: index, // Usamos el índice como ID temporal
        date: formatDate(tx.date),
        promotion_name: tx.coupon,
        client_name: tx.client_name,
        status: 'Canjeado', // Asumimos que todas las transacciones son canjes
        promo_code: tx.promo_code,
        client_id: tx.client_id
      }));
      
      setConsumerPromotions(formattedTransactions);
    } catch (err) {
      console.error('Error al cargar promociones del consumidor:', err);
      setPromotionsError('No se pudieron cargar las promociones del consumidor');
      enqueueSnackbar('Error al cargar las promociones del consumidor', { variant: 'error' });
    } finally {
      setPromotionsLoading(false);
    }
  };

  // Abrir diálogo de transacciones
  const handleOpenPromotionsDialog = (consumerId: string) => {
    setSelectedConsumerId(consumerId);
    fetchConsumerTransactions(consumerId);
  };

  // Cerrar diálogo de promociones
  const handleClosePromotionsDialog = () => {
    setSelectedConsumerId(null);
    setConsumerPromotions([]);
  };

  const handleExportPromotionsToExcel = () => {
    if (consumerPromotions.length === 0) return;
    
    // Formatear los datos para Excel
    const dataToExport = consumerPromotions.map(promo => ({
      'FECHA': formatDate(promo.date),
      'PROMOCIÓN': promo.promotion_name,
      'CLIENTE': promo.client_name,
      'ESTADO': promo.status
    }));
    
    // Crear libro de Excel
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    
    // Ajustar el ancho de las columnas
    const wscols = [
      { wch: 20 }, // FECHA
      { wch: 30 }, // PROMOCIÓN
      { wch: 30 }, // CLIENTE
      { wch: 15 }  // ESTADO
    ];
    ws['!cols'] = wscols;
    
    // Agregar hoja al libro
    XLSX.utils.book_append_sheet(wb, ws, 'Promociones');
    
    // Generar archivo y descargar
    const consumer = consumers.find(c => c.uid === selectedConsumerId);
    const consumerName = consumer ? `${consumer.first_name} ${consumer.last_name || ''}`.trim() : 'consumidor';
    const fileName = `Promociones_${consumerName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    
    // Mostrar notificación
    enqueueSnackbar('Exportación a Excel completada', { variant: 'success' });
  };

  // Exportar a Excel
  const handleExportToExcel = () => {
    // Preparar los datos para la exportación
    const dataToExport = filteredConsumers.map(consumer => ({
      'NOMBRE': consumer.first_name,
      'APELLIDO': consumer.last_name,
      'EMAIL': consumer.email,
      'PROMOCIONES USADAS': consumer.burned_coupons || 0,
      'ÚLTIMO CANJE': consumer.last_burned_coupon || 'Sin canjes',
      'ESTADO': consumer.enabled ? 'Activo' : 'Inactivo'
    }));

    // Crear un libro de trabajo y una hoja
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    
    // Ajustar el ancho de las columnas
    const columnWidths = [
      { wch: 20 }, // NOMBRE
      { wch: 20 }, // APELLIDO
      { wch: 30 }, // EMAIL
      { wch: 15 }, // PROMOCIONES USADAS
      { wch: 25 }, // ÚLTIMO CANJE
      { wch: 30 }, // CLIENTE DEL ÚLTIMO CANJE
      { wch: 15 }, // ESTADO
      { wch: 25 }, // FECHA DE REGISTRO
      { wch: 25 }  // ÚLTIMA ACTUALIZACIÓN
    ];
    ws['!cols'] = columnWidths;
    
    // Agregar la hoja al libro
    XLSX.utils.book_append_sheet(wb, ws, 'Consumidores');
    
    // Generar el archivo Excel y descargarlo
    const fileName = `consumidores_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Efecto para cargar los datos iniciales
  useEffect(() => {
    const fetchConsumers = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Iniciando carga de consumidores...');
        const data = await getConsumers();
        
        // Verificar y formatear los datos para el DataGrid
        const formattedData = data.map(consumer => {
          // Asegurarse de que cada registro tenga un id único (requerido por DataGrid)
          if (!consumer.uid) {
            console.warn('Consumidor sin uid:', consumer);
          }
          
          // Verificar si hay información de último canje
          let lastExchange = null;
          
          // Verificar si hay cupones quemados
          const hasCoupons = consumer.burned_coupons && consumer.burned_coupons > 0;
          
          // Verificar si last_burned_coupon tiene un valor válido
          const lastBurnedCoupon = consumer.last_burned_coupon;
          
          // Validar que last_burned_coupon no sea null, undefined, string vacío, o valores inválidos
          const isValidDate = lastBurnedCoupon && 
                             typeof lastBurnedCoupon === 'string' && 
                             lastBurnedCoupon.trim() !== '' &&
                             lastBurnedCoupon !== 'null' &&
                             lastBurnedCoupon !== 'undefined' &&
                             lastBurnedCoupon !== '0000-00-00' &&
                             lastBurnedCoupon !== '0000-00-00 00:00:00';
          
          console.log(`Procesando último canje para ${consumer.first_name} ${consumer.last_name}:`, {
            last_burned_coupon: lastBurnedCoupon,
            burned_coupons: consumer.burned_coupons,
            isValidDate,
            client_name: consumer.client_name
          });
          
          if (isValidDate) {
            // Tiene una fecha válida de último canje
            lastExchange = {
              date: lastBurnedCoupon,
              client_name: consumer.client_name || 'Cliente no especificado'
            };
            console.log(`Último canje creado para ${consumer.first_name}:`, lastExchange);
          } else if (hasCoupons) {
            // Si tiene cupones quemados pero no fecha válida de último canje, marcar para verificación
            console.log(`INCONSISTENCIA DETECTADA: ${consumer.first_name} ${consumer.last_name} tiene ${consumer.burned_coupons} canjes pero sin fecha válida de último canje`);
            lastExchange = {
              date: 'Verificar transacciones',
              client_name: 'Datos inconsistentes - Verificar'
            };
          } else {
            // No tiene cupones quemados ni fecha de último canje
            console.log(`Sin último canje para ${consumer.first_name} ${consumer.last_name}`);
          }
          
          return {
            ...consumer,
            id: consumer.uid, // Asegurar que cada registro tenga un id único
            promotions_used: consumer.burned_coupons || 0,
            last_exchange: lastExchange
          };
        });
        
        console.log('Datos formateados para DataGrid:', formattedData);
        setConsumers(formattedData);
        setFilteredConsumers(formattedData);
      } catch (err) {
        console.error('Error al cargar consumidores:', err);
        setError('Error al cargar la lista de consumidores');
        enqueueSnackbar('Error al cargar los consumidores', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchConsumers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar una vez al montar el componente

  // Aplicar filtros a las columnas
  const applyFilters = useCallback((data: Consumer[]) => {
    if (!filterModel.items.length) return data;

    return data.filter((row) => {
      return filterModel.items.every((filterItem) => {
        if (!filterItem.field || filterItem.value === undefined || filterItem.value === '') return true;
        
        const columnValue = row[filterItem.field as keyof Consumer];
        if (columnValue === undefined) return false;

        const filterValue = String(filterItem.value).toLowerCase();
        
        // Manejar diferentes tipos de filtrado según la columna
        switch (filterItem.field) {
          case 'status':
            return String(columnValue) === filterValue;
          case 'promotions_used':
            return String(columnValue).includes(filterValue);
          case 'last_exchange':
            const lastExchange = columnValue as { date?: string; client_name?: string } | undefined;
            if (!lastExchange) return filterValue === 'sin canjes';
            return (
              (lastExchange.client_name?.toLowerCase() || '').includes(filterValue) ||
              (lastExchange.date?.toLowerCase() || '').includes(filterValue)
            );
          default:
            return String(columnValue).toLowerCase().includes(filterValue);
        }
      });
    });
  }, [filterModel]);

  // Aplicar búsqueda global
  const applyGlobalSearch = useCallback((data: Consumer[], search: string) => {
    if (!search.trim()) return data;
    
    const searchLower = search.toLowerCase();
    return data.filter(
      (consumer) =>
        consumer.first_name.toLowerCase().includes(searchLower) ||
        consumer.last_name.toLowerCase().includes(searchLower) ||
        consumer.email.toLowerCase().includes(searchLower) ||
        `${consumer.first_name} ${consumer.last_name}`.toLowerCase().includes(searchLower) ||
        (consumer.enabled ? 'activo' : 'inactivo').includes(searchLower) ||
        (consumer.burned_coupons && String(consumer.burned_coupons).includes(searchLower))
    );
  }, []);

  // Efecto para aplicar filtros cuando cambian los datos o los filtros
  useEffect(() => {
    let result = [...consumers];
    
    // Aplicar búsqueda global si existe
    if (searchText) {
      result = applyGlobalSearch(result, searchText);
    }
    
    // Aplicar filtros de columnas
    if (filterModel.items.length > 0) {
      result = applyFilters(result);
    }
    
    setFilteredConsumers(result);
  }, [consumers, searchText, filterModel, applyFilters, applyGlobalSearch]);

  // Manejador para actualizar el estado de un consumidor con confirmación
  const handleStatusChange = (uid: string, enabled: boolean) => {
    // Encontrar el consumidor actual
    const consumer = consumers.find(c => c.uid === uid);
    if (!consumer) return;
    
    // Mostrar diálogo de confirmación personalizado
    setConfirmationDialog({
      open: true,
      title: 'Confirmar cambio de estado',
      message: `¿Estás seguro de que deseas ${enabled ? 'activar' : 'desactivar'} al consumidor?`,
      onConfirm: async () => {
        try {
          setUpdatingStatus(uid);
          // Actualizado para usar el nuevo formato de la API
          const success = await updateConsumerStatus(uid, !enabled);
          
          if (success) {
            // Actualizar el estado local
            const updatedConsumers = consumers.map(consumer => 
              consumer.uid === uid 
                ? { ...consumer, enabled } 
                : consumer
            );
            
            setConsumers(updatedConsumers);
            enqueueSnackbar('Estado actualizado correctamente', { variant: 'success' });
          } else {
            throw new Error('Error al actualizar el estado');
          }
        } catch (error) {
          console.error('Error al actualizar estado:', error);
          enqueueSnackbar('Error al actualizar el estado', { variant: 'error' });
        } finally {
          setUpdatingStatus(null);
        }
      }
    });
  };
  
  // Cerrar el diálogo de confirmación
  const handleCloseConfirmationDialog = () => {
    setConfirmationDialog(prev => ({
      ...prev,
      open: false,
      onConfirm: null
    }));
  };

  // Configuración de columnas con filtros
  const columns: GridColDef[] = [
    { 
      field: 'first_name', 
      headerName: 'NOMBRE', 
      flex: 1,
      minWidth: 150,
      filterable: true,
      filterOperators: [
        {
          label: 'Contiene',
          value: 'contains',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.field || !filterItem.value) {
              return null;
            }
            return (params) => {
              return String(params.value).toLowerCase().includes(String(filterItem.value).toLowerCase());
            };
          },
          InputComponent: CustomFilterInput,
        },
      ],
      renderCell: (params) => (
        <Typography variant="body1" component="div" sx={{ fontSize: '1.1rem', lineHeight: 1.2, fontWeight: 'normal' }}>
          {params.value}
        </Typography>
      ),
    },
    { 
      field: 'last_name', 
      headerName: 'APELLIDO', 
      flex: 1,
      minWidth: 150,
      filterable: true,
      filterOperators: [
        {
          label: 'Contiene',
          value: 'contains',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.field || !filterItem.value) {
              return null;
            }
            return (params) => {
              return String(params.value).toLowerCase().includes(String(filterItem.value).toLowerCase());
            };
          },
          InputComponent: CustomFilterInput,
        },
      ],
      renderCell: (params) => (
        <Typography variant="body1" component="div" sx={{ fontSize: '1.1rem', lineHeight: 1.2, fontWeight: 'normal' }}>
          {params.value}
        </Typography>
      ),
    },
    { 
      field: 'email', 
      headerName: 'EMAIL', 
      flex: 1.5,
      minWidth: 200,
      filterable: true,
      filterOperators: [
        {
          label: 'Contiene',
          value: 'contains',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.field || !filterItem.value) {
              return null;
            }
            return (params) => {
              return String(params.value).toLowerCase().includes(String(filterItem.value).toLowerCase());
            };
          },
          InputComponent: CustomFilterInput,
        },
      ],
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          {params.value}
        </Typography>
      ),
    },
    { 
      field: 'promotions_used', 
      headerName: 'PROMOS USADAS', 
      type: 'number',
      flex: 0.8,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value: any, row: any) => row?.burned_coupons || 0,
      filterable: true,
      filterOperators: [
        {
          label: 'Igual a',
          value: '=',
          getApplyFilterFn: (filterItem) => {
            if (filterItem.value === undefined || filterItem.value === '') {
              return null;
            }
            return (params) => {
              return Number(params.value) === Number(filterItem.value);
            };
          },
          InputComponent: CustomFilterInput,
        },
        {
          label: 'Mayor que',
          value: '>',
          getApplyFilterFn: (filterItem) => {
            if (filterItem.value === undefined || filterItem.value === '') {
              return null;
            }
            return (params) => {
              return Number(params.value) > Number(filterItem.value);
            };
          },
          InputComponent: CustomFilterInput,
        },
        {
          label: 'Menor que',
          value: '<',
          getApplyFilterFn: (filterItem) => {
            if (filterItem.value === undefined || filterItem.value === '') {
              return null;
            }
            return (params) => {
              return Number(params.value) < Number(filterItem.value);
            };
          },
          InputComponent: CustomFilterInput,
        },
      ],
      renderCell: (params: any) => (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Chip
            label={params.value}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ fontWeight: 500, minWidth: 40 }}
          />
        </Box>
      ),
    },
    { 
      field: 'last_exchange', 
      headerName: 'ÚLTIMO CANJE', 
      flex: 1.5,
      minWidth: 250,
      filterable: true,
      filterOperators: [
        {
          label: 'Contiene',
          value: 'contains',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.field || !filterItem.value) {
              return null;
            }
            return (params) => {
              const lastExchange = params.value as { date?: string; client_name?: string } | undefined;
              if (!lastExchange) return false;
              return (
                String(lastExchange.client_name || '').toLowerCase().includes(String(filterItem.value).toLowerCase()) ||
                String(lastExchange.date || '').toLowerCase().includes(String(filterItem.value).toLowerCase())
              );
            };
          },
          InputComponent: CustomFilterInput,
        },
      ],
      renderCell: (params) => {
        const lastExchange = params.value as { date?: string; client_name?: string } | undefined;
        const consumer = params.row;
        
        // Si no hay información de último canje
        if (!lastExchange?.date) {
          return (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              Sin canjes
            </Typography>
          );
        }
        
        // Caso especial: datos inconsistentes
        if (lastExchange.date === 'Verificar transacciones') {
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2, color: 'warning.main' }}>
                Datos inconsistentes
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  fontSize: '0.75rem',
                  fontStyle: 'italic',
                  lineHeight: 1.2
                }}
              >
                {consumer.burned_coupons} canjes registrados
              </Typography>
            </Box>
          );
        }
        
        const formattedDate = formatDate(lastExchange.date);
        
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
              {formattedDate}
            </Typography>
            {lastExchange.client_name && 
             lastExchange.client_name !== 'Cliente no especificado' && 
             lastExchange.client_name !== 'Datos inconsistentes - Verificar' ? (
              <Tooltip title={lastExchange.client_name} arrow>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    fontSize: '0.75rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                    lineHeight: 1.2
                  }}
                >
                  {lastExchange.client_name}
                </Typography>
              </Tooltip>
            ) : (
              <Typography 
                variant="body2" 
                color="text.disabled" 
                sx={{ 
                  fontSize: '0.75rem',
                  fontStyle: 'italic',
                  lineHeight: 1.2
                }}
              >
                Cliente no disponible
              </Typography>
            )}
          </Box>
        );
      },
    },
    { 
      field: 'status', 
      headerName: 'ESTADO', 
      flex: 1.2,
      minWidth: 200,
      filterable: true,
      filterOperators: [
        {
          label: 'Igual a',
          value: 'is',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.field || !filterItem.value) {
              return null;
            }
            return (params) => {
              return String(params.value) === String(filterItem.value);
            };
          },
          InputComponent: StatusFilterInput,
        },
      ],
      renderCell: (params) => {
        const isActive = params.row.enabled;
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={params.row.enabled}
                onChange={() => handleStatusChange(params.row.uid, !params.row.enabled)}
                color={params.row.enabled ? 'success' : 'default'}
                disabled={updatingStatus === params.row.uid}
                size="small"
              />
              <Typography variant="body2" sx={{ 
                color: isActive ? 'success.main' : 'text.secondary',
                fontWeight: 500,
                minWidth: 70,
                textAlign: 'center'
              }}>
                {isActive ? 'Activo' : 'Inactivo'}
              </Typography>
            </Box>
            
            <Tooltip title="Ver promociones utilizadas">
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenPromotionsDialog(params.row.id);
                }}
                sx={{
                  color: 'rgba(0, 0, 0, 0.7)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    color: 'rgba(0, 0, 0, 0.9)',
                  },
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // Manejar cambios en los filtros
  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
  };

  // Limpiar búsqueda
  const clearSearch = useCallback(() => {
    setSearchText('');
  }, []);

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontSize: '28px',
            fontWeight: 600, 
            color: '#C8102E',
            mb: 0
          }}
        >
          Listado de consumidores
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2 }}>
          <Box sx={{ flex: 1, maxWidth: 500 }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Buscar en todos los campos..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={clearSearch}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      <SearchClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          
          <Button
            variant="outlined"
            startIcon={<DownloadIcon sx={{ color: '#C8102E' }} />}
            onClick={handleExportToExcel}
            sx={{
              color: '#C8102E',
              backgroundColor: 'white',
              borderColor: '#C8102E',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#C8102E',
                color: 'white',
                borderColor: '#C8102E',
                '& .MuiSvgIcon-root': {
                  color: 'white',
                },
              },
              textTransform: 'none',
              minWidth: '180px',
              height: '40px',
              borderRadius: '4px',
            }}
          >
            Exportar a Excel
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ flexGrow: 1, width: '100%', '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' } }}>
          <DataGrid
            rows={filteredConsumers}
            columns={columns}
            loading={loading}
            pageSizeOptions={[10, 25, 50]}
            getRowId={(row) => row.uid}
            disableRowSelectionOnClick
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            disableColumnMenu={false}
            filterMode="server"
            slotProps={{
              toolbar: {
                showQuickFilter: false,
              },
              filterPanel: {
                sx: {
                  '& .MuiDataGrid-filterForm': {
                    p: 1,
                    gap: 1,
                  },
                  '& .MuiDataGrid-filterFormValueInput': {
                    width: 200,
                  },
                },
              },
            }}
            sx={{
              // Column headers container
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: theme.palette.grey[50],
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
              
              // Column header styles
              '& .MuiDataGrid-columnHeader': {
                padding: '0 8px',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.MuiDataGrid-columnHeader--filtered': {
                  backgroundColor: theme.palette.action.selected,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                },
                // Focus styles for column headers
                '&:focus, &:focus-within': {
                  outline: 'none',
                },
              },
              
              // Column header title
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                fontSize: '0.875rem',
              },
              
              // Column header title container
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                width: '100%',
                justifyContent: 'center',
                '& .MuiDataGrid-columnHeaderTitleContainerContent': {
                  width: '100%',
                },
              },
              
              // Sortable column headers
              '& .MuiDataGrid-columnHeader--sortable': {
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              },
              
              // Filter/sort menu icons
              '& .MuiDataGrid-menuIcon': {
                margin: 0,
              },
              '& .MuiDataGrid-menuIconButton': {
                padding: '4px',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: theme.palette.primary.main,
                },
              },
              
              // Table cells
              '& .MuiDataGrid-cell': {
                padding: '0 8px',
                borderBottom: `1px solid ${theme.palette.divider}`,
                // Focus styles for cells
                '&:focus, &:focus-within': {
                  outline: 'none',
                },
              },
              
              // Table rows
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.light,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light,
                  },
                },
              },
            }}
            // Tooltip styling is now handled by the theme overrides
          />
        </Box>
        
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Mostrando {filteredConsumers.length} de {consumers.length} registros
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            sx={{
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            Volver arriba
          </Button>
        </Box>
      </Paper>
      
      {/* Diálogo de promociones del consumidor */}
      <Dialog
        open={!!selectedConsumerId}
        onClose={handleClosePromotionsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {selectedConsumerId && (
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                fontSize: '18px',
                fontWeight: 600,
                color: '#C8102E',
                mb: 1
              }}
            >
              {(() => {
                const consumer = consumers.find(c => c.uid === selectedConsumerId);
                return consumer ? `${consumer.first_name} ${consumer.last_name || ''}`.trim() : '';
              })()}
            </Typography>
          )}
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 1 }} />
          Promociones utilizadas
        </DialogTitle>
        <DialogContent>
          {promotionsLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : promotionsError ? (
            <Alert severity="error">{promotionsError}</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>FECHA</strong></TableCell>
                    <TableCell><strong>PROMOCIÓN</strong></TableCell>
                    <TableCell><strong>CÓDIGO</strong></TableCell>
                    <TableCell><strong>CLIENTE</strong></TableCell>
                    <TableCell align="right"><strong>ESTADO</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {consumerPromotions.length > 0 ? (
                    consumerPromotions.map((promo) => (
                      <TableRow key={`${promo.id}-${promo.date}`} hover>
                        <TableCell>{promo.date}</TableCell>
                        <TableCell>{promo.promotion_name}</TableCell>
                        <TableCell>
                          <Chip 
                            label={promo.promo_code} 
                            size="small" 
                            variant="outlined"
                            sx={{ fontFamily: 'monospace' }}
                          />
                        </TableCell>
                        <TableCell>{promo.client_name}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={promo.status}
                            size="small"
                            color={promo.status === 'Canjeado' ? 'success' : 'default'}
                            variant={promo.status === 'Canjeado' ? 'filled' : 'outlined'}
                            sx={{ 
                              minWidth: 90,
                              fontWeight: 500 
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <Typography color="textSecondary">
                          No se encontraron promociones utilizadas
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            variant="outlined"
            onClick={handleExportPromotionsToExcel}
            disabled={consumerPromotions.length === 0}
            startIcon={<DownloadIcon sx={{ color: consumerPromotions.length > 0 ? '#C8102E' : 'rgba(0, 0, 0, 0.26)' }} />}
            sx={{
              color: '#C8102E',
              backgroundColor: 'white',
              borderColor: '#C8102E',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#C8102E',
                color: 'white',
                borderColor: '#C8102E',
                '& .MuiSvgIcon-root': {
                  color: 'white',
                },
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(200, 16, 46, 0.1)',
                color: 'rgba(200, 16, 46, 0.5)',
                borderColor: 'rgba(200, 16, 46, 0.3)',
              },
              textTransform: 'none',
              minWidth: '180px',
              height: '36px',
              borderRadius: '4px',
              mr: 'auto'
            }}
          >
            Exportar a Excel
          </Button>
          <Button 
            onClick={handleClosePromotionsDialog}
            variant="outlined"
            sx={{
              color: '#757575',
              borderColor: '#BDBDBD',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderColor: '#9E9E9E',
                color: '#424242',
              },
              textTransform: 'none',
              fontWeight: 500,
              minWidth: '100px',
              height: '36px',
              borderRadius: '4px',
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación para cambio de estado */}
      <Dialog
        open={confirmationDialog.open}
        onClose={handleCloseConfirmationDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{confirmationDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmationDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={handleCloseConfirmationDialog}
            variant="outlined"
            sx={{
              color: '#757575',
              borderColor: '#BDBDBD',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderColor: '#9E9E9E',
                color: '#424242',
              },
              textTransform: 'none',
              fontWeight: 500,
              minWidth: '100px',
              height: '36px',
              borderRadius: '4px',
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={() => {
              if (confirmationDialog.onConfirm) {
                confirmationDialog.onConfirm();
              }
              handleCloseConfirmationDialog();
            }}
            variant="contained"
            color="primary"
            sx={{
              backgroundColor: '#C8102E',
              '&:hover': {
                backgroundColor: '#A50E30',
              },
              textTransform: 'none',
              fontWeight: 500,
              minWidth: '120px',
              height: '36px',
              borderRadius: '4px',
            }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
